import { makeStyles } from '@material-ui/styles'
import { getDomainId, getIDPName } from 'api-client/helpers'
import CopyToClipboard from 'core/components/CopyToClipboard'
import DocumentMeta from 'core/components/DocumentMeta'
import SubmitButton from 'core/components/buttons/SubmitButton'
import Progress from 'core/components/progress/Progress'
import CodeMirror from 'core/components/validatedForm/CodeMirrorField'
import DropdownField from 'core/components/validatedForm/DropdownField'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import TextField from 'core/components/validatedForm/TextField'
import ValidatedForm from 'core/components/validatedForm/ValidatedForm'
import Text from 'core/elements/Text'
import Button from 'core/elements/button'
import useParams from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { customValidator } from 'core/utils/fieldValidators'
import React, { useCallback, useEffect, useState } from 'react'
import AccountUpgradeDialog from '../../theme/AccountUpgradeDialog'
import SsoEnabledDialog from './SsoEnabledDialog'
import SsoProviderPicklist from './SsoProviderPicklist'
import SsoToggle from './SsoToggle'
import {
  addIdp,
  createSsoConfig,
  deleteFederatedUsers,
  deleteSsoConfig,
  getIdps,
  loadSsoConfig,
  updateIdp,
} from './actions'
import { SsoProviders } from './model'
import { getProductName } from 'core/utils/helpers'
import Alert from 'core/components/Alert'
import {
  createSsoConfigBody,
  getErrorMsgFromApiError,
  handleIdpConfiguration,
} from 'app/plugins/account/components/ssoManagement/helper'

const useStyles = makeStyles((theme: Theme) => ({
  validatedFormContainer: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  wizardLabel: {
    margin: theme.spacing(1, 0),
  },
  fullWidth: {
    width: '100% !important',
  },
  spaceBelow: {
    marginBottom: theme.spacing(1.5),
  },
  conditionalFields: {
    borderTop: `1px solid ${theme.components.card.border}`,
    marginTop: 24,
    paddingTop: 16,
    display: 'grid',
    gridGap: theme.spacing(1),
  },
  field: {
    display: 'flex',
    alignItems: 'center',
  },
  orLabel: {
    marginRight: 24,
  },
  attributeMapButtons: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridGap: theme.spacing(2),
  },
  copyIcon: {
    marginRight: theme.spacing(0.5),
  },
  copyButton: {
    marginLeft: theme.spacing(4),
  },
  filename: {
    marginLeft: theme.spacing(0.5),
  },
  error: {
    color: theme.palette.red[500],
  },
  card: {
    overflowY: 'auto',
    maxWidth: 'inherit',
  },
}))

type State = {
  enableSso: boolean
  ssoIsEnabled: boolean
  ssoProviderName: string
  defaultAttributeMap: string
  entityId: string
  ssoProvider: string
  metadataUrl?: string
  // metadata?: string
  // metadataFileName?: string
}

const customCodeMirrorOptions = {
  mode: 'xml',
}

const SsoPage = ({ setIsSSOEnabled }) => {
  const classes = useStyles({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [dialogOpened, setDialogOpened] = useState(false)
  const [upgradeDialogOpened, setUpgradeDialogOpened] = useState(false)
  const [hasIdp, setHasIdp] = useState(false)
  const [currentEntityId, setCurrentEntityId] = useState('') // To store the entityId of the existing IDP
  const [existingEntityIds, setExistingEntityIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const { params, updateParams, getParamsUpdater } = useParams<State>({
    enableSso: false,
    ssoIsEnabled: false,
    defaultAttributeMap: '',
    entityId: '',
    ssoProvider: '',
    ssoProviderName: '',
  })

  const entityIdValidator = customValidator((value: string) => {
    // For updates, if the entityId is the same as the existing one, allow it
    if (currentEntityId === value) {
      return true
    }
    return !existingEntityIds.includes(value.toLowerCase())
  }, 'Identity provider already exists')

  // Set the parent state to true if SSO is enabled
  useEffect(() => {
    setIsSSOEnabled(params.ssoIsEnabled)
  }, [params.ssoIsEnabled])

  const getSettings = async () => {
    try {
      const {
        attr_map_xml: defaultAttributeMap,
        entity_id: entityId,
        provider,
        metadata_url: metadataUrl,
      }: any = await loadSsoConfig()

      // If SSO Provider is not one of the predefined ones, set it to Other
      // and set the name of the provider
      let ssoProvider = provider,
        ssoProviderName = ''
      if (![SsoProviders.Google, SsoProviders.Okta, SsoProviders.OneLogin].includes(provider)) {
        ssoProviderName = provider
        ssoProvider = SsoProviders.Other
      }

      // Set the existing entityId for validation
      setCurrentEntityId(entityId)
      updateParams({
        defaultAttributeMap,
        entityId,
        ssoProvider,
        ssoProviderName,
        metadataUrl,
        enableSso: true,
        ssoIsEnabled: true,
      })
    } catch (err) {
      updateParams({ ssoIsEnabled: false })
    }
    setLoading(false)
  }

  const loadIdps = async () => {
    const idps = await getIdps()
    const idpName = getIDPName()
    const hasIDP = !!idps.find((idp) => idp.id === idpName)
    const existingEntityIds = idps
      ?.flatMap((idp) => idp?.remote_ids || [])
      ?.map((id) => id.toLowerCase())
    setExistingEntityIds(existingEntityIds)
    setHasIdp(hasIDP)
  }

  // Load settings and idps on mount
  useEffect(() => {
    getSettings()
    loadIdps()
  }, [])

  useEffect(() => {
    if (!params.enableSso) {
      setCurrentEntityId('')
      setExistingEntityIds([])
      setHasIdp(false)
      setError(null)
    }
  }, [params.enableSso])

  const createNewSsoConfig = useCallback(async () => {
    setSubmitting(true)
    if (params.ssoIsEnabled) {
      await deleteSsoConfig()
    }
    try {
      const body = createSsoConfigBody(params)
      await createSsoConfig(body)
      await handleIdpConfiguration(hasIdp, params)
      updateParams({ ssoIsEnabled: true })
      setDialogOpened(true)
    } catch (error) {
      if (error?.response?.data) {
        setError(getErrorMsgFromApiError(error))
        return
      }

      setError(
        error?.message ||
          error?.err?.message ||
          'Failed to create SSO configuration. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }, [params, hasIdp])

  const updateExistingSsoConfig = useCallback(async () => {
    setSubmitting(true)
    try {
      const body = createSsoConfigBody(params)
      await createSsoConfig(body)
      await handleIdpConfiguration(hasIdp, params)
      updateParams({ ssoIsEnabled: true })
      // Show user a dialog saying sso configuration successful
      setDialogOpened(true)
    } catch (error) {
      if (error?.response?.data) {
        setError(getErrorMsgFromApiError(error))
        return
      }

      setError(
        error?.message ||
          error?.err?.message ||
          'Failed to update SSO configuration. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }, [params, hasIdp])

  const updateSsoSettings = useCallback(async () => {
    setError(null)
    if (!!currentEntityId) {
      await updateExistingSsoConfig()
    } else {
      await createNewSsoConfig()
    }
    await getSettings() // to update the page with latest state after saving/updating SSO config
    await loadIdps() // to update existing IDPs if not added user cannot update SSO config immediately after creating
  }, [currentEntityId, updateExistingSsoConfig, createNewSsoConfig])

  return (
    <>
      <DocumentMeta title="SSO Management" bodyClasses={['form-view']} />
      {upgradeDialogOpened && (
        <AccountUpgradeDialog
          feature="Enterprise SSO"
          onClose={() => setUpgradeDialogOpened(false)}
        />
      )}
      <Progress loading={loading}>
        <ValidatedForm
          classes={{ root: classes.validatedFormContainer }}
          elevated={false}
          formActions={
            <>
              {params.enableSso && (
                <SubmitButton loading={submitting}>
                  {currentEntityId ? 'Update' : 'Save'}
                </SubmitButton>
              )}
            </>
          }
          onSubmit={() => updateSsoSettings()}
        >
          {error && <Alert variant="error" message={error} title="Error" />}
          <FormFieldCard className={classes.card}>
            {submitting ? (
              <Progress loading={submitting} message="Saving SSO settings..." />
            ) : (
              <>
                <SsoToggle params={params} updateParams={updateParams} />
                <Text variant="body2" className={classes.spaceBelow}>
                  {`Enterprise Single Sign On supports SAML 2.0 identity integration for seamless
                  access to your ${getProductName()} instance.`}
                </Text>
                {params.enableSso && (
                  <div className={classes.conditionalFields}>
                    <DropdownField
                      DropdownComponent={SsoProviderPicklist}
                      id="ssoProvider"
                      label="SSO Provider"
                      onChange={getParamsUpdater('ssoProvider')}
                      value={params.ssoProvider}
                      required
                      disabled={!!currentEntityId} // Disable changing provider if updating existing config
                    />
                    {params.ssoProvider === SsoProviders.Other && (
                      <TextField
                        id="ssoProviderName"
                        label="SSO Provider Name"
                        onChange={getParamsUpdater('ssoProviderName')}
                        value={params.ssoProviderName}
                        info="Provide a name to identify your SSO provider"
                        required
                        disabled={!!currentEntityId} // Disable changing provider if updating existing config
                      />
                    )}
                    <Text variant="caption1" className={classes.wizardLabel}>
                      Entity Endpoint for your SSO Provider
                    </Text>
                    <TextField
                      id="entityId"
                      label="Entity ID"
                      onChange={getParamsUpdater('entityId')}
                      value={params.entityId}
                      required
                      validations={[entityIdValidator]}
                      disabled={!!currentEntityId} // Disable changing provider if updating existing config
                    />
                    {/* <Text variant="caption1" className={classes.wizardLabel}>
                  SAML Metadata (XML) from your SSO Provider
                  <Text variant="body2">
                    You may specify a metadata URL or upload a metadata XML file.
                  </Text>
                </Text> */}
                    <TextField
                      id="metadataUrl"
                      label="SAML Metadata URL"
                      onChange={getParamsUpdater('metadataUrl')}
                      value={params.metadataUrl}
                      required
                      // required={!params.metadata}
                    />
                    {/* <div className={classes.field}>
                  <Text variant="body2" className={classes.orLabel}>
                    OR
                  </Text>
                  <UploadSamlMetadataLink
                    id="metadata"
                    onChange={getParamsUpdater('metadata')}
                    fileNameUpdater={getParamsUpdater('metadataFileName')}
                  />
                  <Text variant="body2" className={classes.filename}>
                    {params.metadataFileName}
                  </Text>
                </div>
                <CodeMirror
                  id="metadata"
                  label="SAML Metadata (XML)"
                  options={customCodeMirrorOptions}
                  onChange={getParamsUpdater('metadata')}
                  value={params.metadata}
                  className={classes.fullWidth}
                  required={!params.metadataUrl}
                />
                {params.metadataUrl && params.metadata && (
                  <Text variant="body2" className={classes.error}>
                    Please specify only the metadata URL or only the metadata XML
                  </Text>
                )} */}
                    <Text variant="caption1" className={classes.wizardLabel}>
                      SSO Provider Attribute MAP in XML
                    </Text>
                    <Text variant="body2">
                      {`Enterprise Single Sign on supports SAML 2.0 identity integration for seamless
                      access to your ${getProductName()} instance.`}
                    </Text>
                    <CodeMirror
                      id="defaultAttributeMap"
                      label="Default Attribute Map"
                      options={customCodeMirrorOptions}
                      onChange={getParamsUpdater('defaultAttributeMap')}
                      value={params.defaultAttributeMap}
                      className={classes.fullWidth}
                      required
                    />
                    <div className={classes.attributeMapButtons}>
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => updateParams({ defaultAttributeMap: '' })}
                      >
                        Clear XML
                      </Button>
                      <CopyToClipboard
                        copyText={params.defaultAttributeMap}
                        copyIcon={false}
                        inline={false}
                        triggerWithChild
                      >
                        {
                          // @ts-ignore
                          <Button type="button" icon="copy">
                            Copy Attribute Map
                          </Button>
                        }
                      </CopyToClipboard>
                    </div>
                  </div>
                )}
              </>
            )}
          </FormFieldCard>
          {dialogOpened && <SsoEnabledDialog onClose={() => setDialogOpened(false)} />}
        </ValidatedForm>
      </Progress>
    </>
  )
}

export default SsoPage
